import type { MouseEvent } from "react";
import type { Shipment } from "api";
import cn from "classnames";
import { CarrierLogo } from "./CarrierLogo";
import { RouteStrip } from "./RouteStrip";
import {
  carrierLabel,
  equipmentLabel,
  isPending,
  loadBadge,
  referenceFor,
  relativeArrivalLabel,
  scheduleLabel,
  shipmentCode,
  statusDetail,
  statusTag,
} from "./shipments.model";

type ShipmentRowProps = {
  shipment: Shipment;
  /** Selection mode is on; the row shows its checkbox and stops navigating. */
  picking: boolean;
  picked: boolean;
  onPick: (id: number, picked: boolean) => void;
  /** The row was clicked outside its controls — see `openShipment`. */
  onOpen: (shipment: Shipment) => void;
  /** Opens the kebab menu against the button that was pressed. */
  onMenu: (shipment: Shipment, anchor: HTMLElement) => void;
  /** This row's kebab menu is the open one. */
  menuOpen: boolean;
};

/** Controls inside the row that handle their own click; the row ignores those. */
const CONTROLS = "button, a, input, label";

export const ShipmentRow = ({ shipment, picking, picked, onPick, onOpen, onMenu, menuOpen }: ShipmentRowProps) => {
  const isAir = shipment.shipment_type === "AIR";
  const documentLabel = isAir ? "Air Waybill" : "Bill of lading";
  const tag = statusTag(shipment);
  const badge = loadBadge(shipment);
  const booking = referenceFor(shipment, "BOOKING");
  const containers = referenceFor(shipment, "CONTAINER");
  const label = isPending(shipment) ? shipmentCode(shipment) : shipment.reference;

  const onRowClick = (event: MouseEvent<HTMLDivElement>) => {
    if (picking) return;
    if ((event.target as HTMLElement).closest(CONTROLS) !== null) return;

    onOpen(shipment);
  };

  return (
    /* The row carries its own buttons, so it cannot itself be one; the kebab's
       "Open shipment" is the keyboard route to the same place. */
    <div className={cn("pcsh__row", { "is-picked": picked })} onClick={onRowClick}>
      <label className="pcsh__pick">
        <input
          type="checkbox"
          className="pcsh__cb"
          checked={picked}
          disabled={!picking}
          onChange={(event) => onPick(shipment.id, event.target.checked)}
          aria-label={`Select ${label}`}
        />
        <i />
      </label>

      <div className="pcsh__c pcsh__c--line" data-l="Line">
        <CarrierLogo shipment={shipment} className="pcsh__cube" size={55} />
        <span className={cn("pcsh__mode", badge.modifier)}>{badge.label}</span>
        <em className="pcsh__sid">{shipmentCode(shipment)}</em>
      </div>

      <div className="pcsh__c" data-l="Schedule">
        <b>{scheduleLabel(shipment)}</b>
        <em>{relativeArrivalLabel(shipment)}</em>
      </div>

      <div className="pcsh__c" data-l={documentLabel}>
        {/* Only one of the two numbers exists: a card carries the reference it
            was tracked by, and inventing the other would be worse than a dash. */}
        {booking !== "—" && <b className="pcsh__bk">{booking}</b>}
        {booking === "—" && <span className="pcsh__mono">{referenceFor(shipment, isAir ? "AWB" : "BL")}</span>}
        <em>{carrierLabel(shipment)}</em>
      </div>

      <div className="pcsh__c" data-l="CNTR/LOT">
        {/* A card tracked by container number names it; the rest only count. */}
        {containers === "—" ? (
          <b>{shipment.equipments_count || "—"}</b>
        ) : (
          <span className="pcsh__mono">{containers}</span>
        )}
        <em>{equipmentLabel(shipment)}</em>
      </div>

      {/* Nothing stores linked products yet, so the cell keeps its empty state. */}
      <div className="pcsh__c" data-l="Product Details">
        <em className="pcsh__dim">No products linked</em>
      </div>

      <div className="pcsh__c" data-l="Status">
        <span className={cn("pc-tag", tag.modifier)}>{tag.label}</span>
        <em>{statusDetail(shipment)}</em>
      </div>

      <button
        type="button"
        className={cn("pcsh__more", { "is-on": menuOpen })}
        aria-label={`More actions for ${label}`}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        onClick={(event) => onMenu(shipment, event.currentTarget)}
      >
        <span />
        <span />
        <span />
      </button>

      <RouteStrip shipment={shipment} className="pcsh__sum" />
    </div>
  );
};
